import useTranslate from "../../../../modules/local/useTranslate";
import React, {useMemo, useState} from "react";
import {notifyOnError} from "../../factory/createErrorEvent";
import useAsyncAction from "../../../../modules/asyncCache/useAsyncAction";
import {
    organization_createAnnouncement_Api,
    organization_deleteAnnouncement_Api,
    organization_updateAnnouncement_Api
} from "../../../../apis";
import {NullAsDefault} from "../../../Shared";
import {NotificationActionTypes, successNotify} from "../../factory/createNotification";
import {showDeleteConfirmDialog} from "../../factory/createConfirmDialog";
import {createModalTitle} from "../../../../components/renderFunctions/createModalTitle";
import {getId} from "../../../../apis/model/base";
import {Modal} from "antd";
import CRUD from "../../../Shared/enums/CRUD";
import {
    OrganizationAnnouncementCreateForm,
    OrganizationAnnouncementUpdateForm
} from "../components/OrganizationAnnouncementForm";


export const useOrganizationAnnouncementApi = ({
                                                   organization,
                                                   organizationDepartments,
                                                   defaultOrganizationDepartment,
                                                   notifyListeners = {
                                                       onCreated: undefined,
                                                       onUpdated: undefined,
                                                       onDeleted: undefined,
                                                   },
                                               }) => {
    const t = useTranslate()

    const [refresh_token, set_refresh_token] = useState()

    const [action, setAction] = useState({
        type: undefined,
        value: undefined,
    })

    const {
        onCreated = NullAsDefault,
        onUpdated = NullAsDefault,
        onDeleted = NullAsDefault,
    } = notifyListeners ?? {}

    const onCancel = () => {
        setAction({})
    }

    const onRefresh = () => {
        onCancel()
        set_refresh_token(Date.now())
    }

    const onError = notifyOnError(t)

    const {handleAsyncAction: onCreate, ...creatingAsync} = useAsyncAction({
        onError,
        apiInfo: organization_createAnnouncement_Api,
        onSuccess: (result, data) => {
            successNotify(NotificationActionTypes.CREATE, t)
            onCreated(result, data)
            onRefresh()
        },
    })

    const {handleAsyncAction: onUpdate, ...updatingAsync} = useAsyncAction({
        onError,
        apiInfo: organization_updateAnnouncement_Api,
        onSuccess: (result, data) => {
            successNotify(NotificationActionTypes.UPDATE, t)
            onUpdated(result, data)
            onCancel()
        },
    })

    const {handleAsyncAction: onDelete} = useAsyncAction({
        onError,
        apiInfo: organization_deleteAnnouncement_Api,
        onSuccess: (result, data) => {
            successNotify(NotificationActionTypes.DELETE, t)
            onDeleted(result, data)
            onRefresh()
        },
    })

    const handleAction = (action, params) => {
        switch (action) {
            case CRUD.CREATE:
            case CRUD.UPDATE:
                setAction({
                    type: action,
                    value: params,
                })
                break
            case CRUD.DELETE:
                showDeleteConfirmDialog({
                    translate: t,
                    title: 'delete this item',
                    message: 'are you sure you want to delete this',
                    onOk: () => {
                        onDelete({}, {id: params?.id})
                    },
                })
            default:
                break
        }
    }

    const modal = useMemo(() => {
        let params = {}
        let modal_params = {}
        let Component = NullAsDefault

        switch (action?.type) {
            case CRUD.CREATE:
                modal_params = {
                    title: createModalTitle('create an announcement'),
                    width: 600,
                }
                params = {
                    initialValues: {
                        ...(action?.value || {}),
                        organization,
                        ...(defaultOrganizationDepartment?.id ? {organization_department_id: defaultOrganizationDepartment?.id} : {})
                    },
                    isSubmitting: creatingAsync.isLoading,
                    onSubmit: (values) => {
                        onCreate(values, {id: getId(organization)})
                    },
                }
                Component = OrganizationAnnouncementCreateForm
                break
            case CRUD.UPDATE:
                const organization_announcement_id = getId(action?.value)
                modal_params = {
                    title: createModalTitle('edit announcement'),
                    width: 600,
                }
                params = {
                    organization_announcement_id,
                    initialValues: {...action?.value},
                    isSubmitting: updatingAsync.isLoading,
                    onSubmit: (values) => {
                        onUpdate(values, {id: organization_announcement_id})
                    },
                }
                Component = OrganizationAnnouncementUpdateForm
                break
            default:
                break
        }

        return (
            <Modal
                footer={null}
                onCancel={onCancel}
                open={action?.type}
                destroyOnHidden={true}
                className="custom-modal"
                {...modal_params}>
                <Component
                    {...{onCancel}}
                    {...params}
                    organization={organization}
                    organizationDepartments={organizationDepartments}
                />
            </Modal>
        )
    }, [action, organization, creatingAsync.isLoading, updatingAsync.isLoading])

    return {
        modal,
        action,
        handleAction,
        refresh_token,
        onRefresh,
    }
}